import { ChevronRight } from 'lucide-react';
import React, { useMemo } from 'react';

import { type AdminApproval, type Role } from '@column/api';
import { Icon } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { CopyRow, CopyRowValue, EntityLink } from '~/components/CopyRow';
import { type OverrideTarget } from '~/lib/approvals/hooks/useApprovalTargets';
import { getRoleDiff, getRoleUpdateDiff } from '~/lib/approvals/roleDiff';
import { type AccountOwners, type PermissionDiff } from '~/lib/approvals/types';
import {
  approvalStatusLabels,
  findRole,
  getEffectiveStatus,
  getRefName,
  getRoleName,
  getUserName,
} from '~/lib/approvals/utils';
import { DashboardUserRedacted } from '~/repositories';
import { getRoleLabel } from '~/repositories/RoleRepository';
import { formatDateTime } from '~/util';

import { PermissionChanges } from './PermissionChanges';
import { type ReviewItemBadge } from './ReviewDialog';

const rolePath = (roleId: string) => `${ROUTE.PLATFORM_ROLES}/${roleId}`;

const RoleName: React.FC<{
  role?: KeysCamelCaseDeep<Role>;
  fallback: string;
  onNavigate: (path: string) => void;
}> = ({ role, fallback, onNavigate }) =>
  role?.editable ? (
    <button
      type="button"
      className="group/link inline-flex max-w-full min-w-0 cursor-pointer items-center gap-0.5 align-bottom font-medium"
      onClick={() => onNavigate(rolePath(role.id))}
    >
      <span className="truncate">{getRoleLabel(role.name)}</span>
      <ChevronRight className="size-3 shrink-0 opacity-0 transition-opacity duration-200 group-hover/link:opacity-100" />
    </button>
  ) : (
    <span>{fallback}</span>
  );

const RoleChangeValue: React.FC<{ from: React.ReactNode; to: React.ReactNode }> = ({ from, to }) => (
  <CopyRowValue className="min-w-0 text-sm leading-8 font-medium text-foreground">
    <span className="line-through">{from}</span>
    <span aria-hidden className="px-1.5 select-none">
      →
    </span>
    {to}
  </CopyRowValue>
);

const emptyDiff: PermissionDiff = { details: [], platform: [], entities: [], bankAccounts: [] };

export const ApprovalDetails: React.FC<{
  approval: AdminApproval;
  roles: KeysCamelCaseDeep<Role>[];
  targets: Map<string, OverrideTarget>;
  accountOwners: AccountOwners;
  onNavigate: (path: string) => void;
}> = ({ approval, roles, targets, accountOwners, onNavigate }) => {
  const request = approval.request;
  const diff = useMemo(() => {
    const { user_role_assign: assign, role_update: update } = approval.request;
    if (assign) return getRoleDiff(findRole(roles, assign.previous), findRole(roles, assign), accountOwners);
    if (update) return getRoleUpdateDiff(update, accountOwners);
    return emptyDiff;
  }, [accountOwners, approval, roles]);
  const permissions = <PermissionChanges diff={diff} targets={targets} onNavigate={onNavigate} />;

  if (request.user_role_assign) {
    const assign = request.user_role_assign;
    return (
      <>
        <CopyRow
          label="Role"
          value={
            <RoleChangeValue
              from={
                <RoleName
                  role={findRole(roles, assign.previous)}
                  fallback={getRefName(roles, assign.previous)}
                  onNavigate={onNavigate}
                />
              }
              to={
                <RoleName role={findRole(roles, assign)} fallback={getRefName(roles, assign)} onNavigate={onNavigate} />
              }
            />
          }
        />
        {permissions}
      </>
    );
  }
  if (request.user_invite) {
    const invite = request.user_invite;
    const role = findRole(roles, { custom_role_id: invite.role_id, precanned_role_name: invite.platform_role });
    return (
      <CopyRow
        label="Role"
        value={
          role?.editable ? (
            <EntityLink label={role.name} onClick={() => onNavigate(rolePath(role.id))} />
          ) : (
            getRoleName(roles, invite.role_id, invite.platform_role)
          )
        }
      />
    );
  }
  if (request.role_update) return permissions;
  if (request.platform_update_require_user_change_approval) {
    const enabled = request.platform_update_require_user_change_approval.require_user_change_approval;
    return (
      <>
        <CopyRow
          label="Rule"
          value={
            <CopyRowValue className="min-w-0 text-sm leading-8 font-medium text-foreground">
              User Access Changes
            </CopyRowValue>
          }
        />
        <CopyRow
          label="Configuration"
          value={<RoleChangeValue from={enabled ? 'Disabled' : 'Enabled'} to={enabled ? 'Enabled' : 'Disabled'} />}
        />
      </>
    );
  }
  return null;
};

export const getApprovalRolePath = (approval: AdminApproval) => {
  const roleId = approval.request.role_update?.role_id;
  return roleId ? rolePath(roleId) : undefined;
};

const getSubject = (
  approval: AdminApproval,
  users: DashboardUserRedacted[],
  roles: KeysCamelCaseDeep<Role>[]
): { title: string; badge?: ReviewItemBadge } => {
  const request = approval.request;
  if (request.user_invite) return { title: request.user_invite.email, badge: { icon: <Icon.Mail />, label: 'Invite' } };
  if (request.user_role_assign) {
    return {
      title: getUserName(users, request.user_role_assign.dashboard_user_id),
      badge: { icon: <Icon.UsersArrow />, label: 'Role change' },
    };
  }
  if (request.role_update) {
    return {
      title: getRoleName(roles, request.role_update.role_id),
      badge: { icon: <Icon.Shield />, label: 'Role update' },
    };
  }
  if (request.platform_update_require_user_change_approval) {
    return { title: 'Approval Rule', badge: { icon: <Icon.Shield />, label: 'Setting change' } };
  }
  return { title: approval.type };
};

export const getApprovalHeader = (
  approval: AdminApproval,
  users: DashboardUserRedacted[],
  roles: KeysCamelCaseDeep<Role>[]
) => {
  const { title, badge } = getSubject(approval, users, roles);
  const badges: ReviewItemBadge[] = [
    ...(badge ? [badge] : []),
    { icon: <Icon.Clock />, label: approvalStatusLabels[getEffectiveStatus(approval)] },
    {
      icon: <Icon.Calendar />,
      label: `Expires at ${formatDateTime(approval.expires_at, { timeZoneFormat: 'local' })}`,
    },
  ];
  return { title, badges };
};
